import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useTheme } from '@mui/material/styles';
import { MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { getLeadChart } from '../../api/dashboard';
import { useBranchFilter } from '../../context/branchContext';
import { ChartPeriod } from '../../types/dashboard';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const PERIODS: ChartPeriod[] = ['daily', 'weekly', 'monthly', 'yearly'];

export const LeadTrendChart = () => {
  const theme = useTheme();
  const [period, setPeriod] = useState<ChartPeriod>('daily');
  const { selectedBranchId } = useBranchFilter();

  const chartQuery = useQuery({
    queryKey: ['lead-chart', period, selectedBranchId],
    queryFn: () => getLeadChart(period, selectedBranchId),
  });

  const gridColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const textColor = theme.palette.text.secondary;

  const data = {
    labels: chartQuery.data?.map((b) => b.label) ?? [],
    datasets: [
      {
        label: 'New leads',
        data: chartQuery.data?.map((b) => b.count) ?? [],
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}22`,
        borderWidth: 2,
        pointRadius: 3,
        pointBackgroundColor: theme.palette.primary.main,
        tension: 0.3,
        fill: true,
      },
    ],
  };

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1">Lead volume</Typography>
        <TextField select size="small" value={period} onChange={(e) => setPeriod(e.target.value as ChartPeriod)}>
          {PERIODS.map((p) => (
            <MenuItem key={p} value={p}>
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Line
        data={data}
        options={{
          responsive: true,
          plugins: { legend: { display: false } },
          scales: {
            x: { grid: { color: gridColor }, ticks: { color: textColor } },
            y: { grid: { color: gridColor }, ticks: { color: textColor }, beginAtZero: true },
          },
        }}
        height={90}
      />
    </Paper>
  );
};
